import { almacenAxios } from '@/services/Axios';
import ClientDeliverySerializer from '@/serializers/ClientDelivery.serializer';
import { ErrorResponse } from '@/common/models/ErrorResponse';

const resource = '/v1/delivery';

export default {
  /* eslint-disable no-console */
  /**
   * Obtiene las ventas con mercancía pendiente de entregar.
   * @param {Object} params
   */
  async getPendingDeliveries(params) {
    try {
      const response = await almacenAxios.get(`${resource}/pending`, { params });

      // Asumiendo que tu FastAPI devuelve { status: "success", data: [...] }
      if (response.data && response.data.status === 'success') {
        const rawData = response.data.data;

        // Retornamos la data ya procesada por el serializador
        return {
          success: true,
          data: ClientDeliverySerializer.fromJsonList(rawData.items || []),
          totalRecords: rawData.total_records || 0,
        };
      }
      return { success: false, data: [], totalRecords: 0 };
    } catch (error) {
      console.error(error);
      throw (new ErrorResponse(error.response.data));
    }
  },

  async getReadyToDeliverCount(storeId) {
    try {
      const response = await almacenAxios.get(`${resource}/pending/ready-count`, {
        params: { store_id: storeId },
      });

      if (response.data && response.data.status === 'success') {
        return response.data.data.ready_count;
      }
      return 0;
    } catch (error) {
      console.error('Error obteniendo el contador:', error);
      return 0;
    }
  },

  async getOrderDetails(folio, storeId, onlyPending = true) {
    try {
      const response = await almacenAxios.get(`${resource}/details/${folio}`, {
        params: {
          store_id: storeId,
          only_pending: onlyPending,
        },
      });
      return response.data;
    } catch (error) {
      console.error(`Error al obtener detalles del folio ${folio}:`, error);
      throw error; // Lanzamos el error para que la vista lo atrape y muestre el SweetAlert
    }
  },

  async confirmDelivery(payload) {
    try {
      // Ajusta la ruta base `${resource}` si es diferente en tu configuración
      const response = await almacenAxios.post(`${resource}/confirm`, payload);
      return response.data;
    } catch (error) {
      console.error('Error al confirmar el despacho:', error);
      throw error; // Lanzamos el error para que el componente lo atrape
    }
  },

  /**
   * Obtiene el historial de mercancías que ya han recibido al menos una entrega física.
   * @param {Object} params (store_id, search_query, start_date, end_date, limit, offset)
   */
  async getDeliveryHistory(params) {
    try {
      // Apunta al nuevo endpoint de historial que creamos en FastAPI
      const response = await almacenAxios.get(`${resource}/history`, { params });

      if (response.data && response.data.status === 'success') {
        return {
          success: true,
          total_records: response.data.data.total_records,
          results: response.data.data.results,
        };
      }
      return { success: false, total_records: 0, results: [] };
    } catch (error) {
      console.error('Error al consultar el historial logístico:', error);
      throw (new ErrorResponse(error.response?.data));
    }
  },

  /**
   * Obtiene el desglose analítico completo de un resguardo (Cabecera, Balance y Timeline)
   * @param {Number|String} folio
   * @param {Number} storeId
   */
  async getHistoryOrderDetails(folio, storeId) {
    try {
      const response = await almacenAxios.get(`${resource}/history/details/${folio}`, {
        params: { store_id: storeId },
      });
      return response.data;
    } catch (error) {
      console.error(`Error al obtener historial del folio ${folio}:`, error);
      throw error;
    }
  },

  /**
   * Obtiene el expediente logístico y financiero completo para el modal de detalle
   * @param {number} folio
   * @param {number} storeId
   */
  async getDeliveryOrderDetail(folio, storeId) {
    try {
      const response = await almacenAxios.get(`${resource}/order/${folio}/details`, {
        params: { store_id: storeId },
      });
      return response.data; // Retorna { status: 'success', data: { ... } }
    } catch (error) {
      console.error('Error al obtener el detalle de la orden:', error);
      throw error;
    }
  },

  async getDeliveryTicketData(folio) {
    try {
      const response = await almacenAxios.get(`${resource}/ticket/preview/${folio}`);
      return response.data;
    } catch (error) {
      console.error(`Error al obtener los datos del ticket de entrega ${folio}:`, error);
      throw error;
    }
  },
};
